// Session 78a. One label, one PDF page, at the label's real physical size.
//
// WHY THIS EXISTS, and it is not a preference.
//
// Every browser-print path in this app used to build an HTML document whose
// page size was declared in CSS:
//
//     @page { size: 50.8mm 24mm; }
//
// That rule is correct and it never reached paper. Chrome honours `@page size`
// when the destination is its own built-in "Save as PDF", and IGNORES it for
// every Windows print driver - there it takes the paper from the driver and
// uses the CSS only to choose portrait or landscape.
//
// Measured at Session 78a on Colby's x64 desktop, same document, same button,
// two destinations:
//
//     Chrome "Save as PDF"        -> MediaBox 0 0 144 72   = 2.00 x 1.00 in  correct
//     "Microsoft Print to PDF"    -> MediaBox 0 0 792 612  = 11.00 x 8.50 in wrong
//
// 11 x 8.5 is Letter, landscape - landscape because our label is wider than it
// is tall, which is the ONLY thing the browser took from our stylesheet. The
// Brother PT-E560BT behaved identically: every path printed onto a ~4-7/8 inch
// strip of 24mm TZe tape with the artwork shrunk to fit, so a 15mm QR arrived
// at about 1/8 inch and the text was unreadable.
//
// A PDF carries its page size inside the file. Nothing downstream reinterprets
// it. So the label is now drawn into a PDF page sized in inches, which is the
// same mechanism the Canon Colorado path has used since Session 47 and the
// reason that path was never affected.
//
// WHAT THIS CLOSES BY CONSTRUCTION: #328.
//
// The HTML templates interpolated item name, SKU, UOM, branch, serial, tenant
// name and station straight from the database into markup, and Session 75 had
// to add an htmlEscape() call at nine sites to stop a `<script>` in an item
// name reaching a print document. `htmlEscape` is deleted with those templates.
// jsPDF's doc.text() writes literal glyphs into a content stream - there is no
// markup context for a payload to break out of, and no parser to break it out
// with. The escaping is not missing here; the vector is.
//
// This is #322's lesson arriving for the third time. That issue found the
// profile table was decorative because the Brother paths hardcoded their own
// millimetre pairs; Session 76 found the format ladder could not reach the page
// size; this found that the page size could not reach the printer. Correcting
// the number is worthless while the channel carrying it is ignored.

import { getFormat } from '../lib/labelFormats';
import { drawLabelTile } from './canonM5Export';

// One page per label. `pages` is already expanded - the caller multiplies out
// copies before calling, exactly as exportCanonM5PDF's `items` contract does,
// so neither module has to know what a "copy" means.
//
// Returns a Blob rather than triggering a download or a print. Choosing what
// happens to the bytes belongs to the caller: printLabel.js prints them,
// and a future export path could save them without this function changing.
export async function buildLabelPdfBlob({
  pages, branchName, formatId, subjectType = 'inventory', mediaHeightIn = null,
}) {
  if (!pages || pages.length === 0) return { blob: null, error: 'no_pages' };

  const format = getFormat(formatId);
  if (!format) return { blob: null, error: 'unknown_format' };

  // The format says how big the LABEL is. The media says how much of it the
  // machine can actually put ink on, and on continuous tape those are not the
  // same number - a 24mm tape prints a band narrower than 24mm, and the format
  // calls it an inch. Whichever is smaller governs, because the difference is
  // not "a bit of white margin", it is content the machine silently discards.
  //
  // Session 78a's second test print came off the Brother with the item name
  // missing, and it took the QR's top quiet zone with it - which is why the
  // in-app scanner then refused a code a phone camera still read. One number,
  // two symptoms.
  const widthIn = format.widthIn;
  const heightIn = mediaHeightIn ? Math.min(format.heightIn, mediaHeightIn) : format.heightIn;

  let jsPDF;
  try {
    ({ jsPDF } = await import('jspdf'));
  } catch (e) {
    console.error('jsPDF init failed:', e);
    return { blob: null, error: 'pdf_init_failed' };
  }

  // `orientation` is derived rather than passed. jsPDF applies it ON TOP of
  // `format`, so declaring 'landscape' for a 2x1 would swap the pair back to
  // 1x2 and reintroduce the exact bug this file exists to fix. The format IS
  // the orientation.
  const doc = new jsPDF({
    orientation: widthIn >= heightIn ? 'landscape' : 'portrait',
    unit: 'in',
    format: [widthIn, heightIn],
  });

  pages.forEach(({ record, qrDataUrl }, i) => {
    if (i > 0) doc.addPage([widthIn, heightIn], widthIn >= heightIn ? 'landscape' : 'portrait');
    // Two switches, both describing the machine rather than the label:
    //
    //   cutGuide off - the Canon sheet is laminated and cut by hand, so its
    //   tiles carry a dashed guide. A Brother label is already the right shape
    //   when it leaves the machine; a border here just prints a rectangle.
    //
    //   monochrome on - thermal transfer has one ink and no halftone, so the
    //   tile's grey ramp dithers into mush at label sizes. Found on the first
    //   correctly-sized label off the PT-E560BT: the item name was crisp and
    //   the branch and tenant lines could not be read at all.
    drawLabelTile(
      doc, 0, 0, widthIn, heightIn,
      record, branchName, qrDataUrl, formatId, subjectType,
      { cutGuide: false, monochrome: true }
    );
  });

  try {
    return { blob: doc.output('blob'), error: null };
  } catch (e) {
    console.error('PDF serialisation failed:', e);
    return { blob: null, error: 'pdf_output_failed' };
  }
}
